import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { chartColors } from '../../helpers/chartColors';
import savitzkyGolay from 'ml-savitzky-golay';

export const RTFOPressureAnalysisChart = ({ currentRTFO, monitorUWI, monitorWellEvents, updateMonitorWellEvents, eventEditMode, gridView, showSpikeLines, showLegend, darkMode, theme, updateMonitorWellEventsMap, showBackgroundMonitors=true }) => {

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const pumpingData = currentStage?.coldboreData;
  const monitorData = currentStage?.monitorData?.[monitorUWI];
  const client = currentRTFO?.client;

  const minMonitorPressure = monitorData?.length > 0 ? Math.floor(Math.min(...monitorData.map(d => d.casingPressure * 1000)?.filter(d => d > 0 && isFinite(d) && !isNaN(d)))) : 0;
  const maxMonitorPressure = Math.max(monitorData?.length > 0 ? Math.ceil(Math.max(...monitorData.map(d => d.casingPressure * 1000)?.filter(d => d > 0 && isFinite(d) && !isNaN(d)))) : 5, minMonitorPressure + 5);

  const smoothedData = useMemo(() => {
    try {
      if (!currentStage?.highResolutionData) return [];
  
      const optionsLong = {
        derivative: 0,
        windowSize: 121,
        polynomial: 1,
        pad: 'pre',
        padValue: 'symmetric'
      };
  
      const YCasingPressure = monitorData?.map(d => (d.casingPressure));
  
      if (!YCasingPressure?.length > optionsLong.windowSize) return [];
      
      let smoothedData = [];
  
      try {
        savitzkyGolay(YCasingPressure, 1, optionsLong);
        smoothedData = savitzkyGolay(smoothedData, 1, optionsLong);
        smoothedData = savitzkyGolay(smoothedData, 1, optionsLong);
      } catch(error) {
        return [];
      }
  
      return smoothedData;

    } catch(error) {
      console.log(error);
      return [];
    };
  }, [currentStage?.highResolutionData, monitorData])

  const data = useMemo(() => {
    if (!pumpingData || pumpingData?.length === 0) {
      return [];
    };

    return getChartData();

    function getChartData() {
      try {
  
        const X = [];
        const YPressure = [];
        const YRate = [];
        const YCumVol = [];
  
        pumpingData?.forEach((d, i) => {
          X.push(new Date(d.timestamp));
          YPressure.push(d.pressure > 0 ? d.pressure : null);
          YRate.push(d.rate > 0 ? d.rate : null);
          YCumVol.push(d.slurry > 0 ? d.slurry : null);
        });
        
        const traces = [];


        traces.push(
          { // treating pressure 
            x: X,
            y: YPressure,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: 'Treating Pr. (MPa)',
            line: {
              width: 2,
              color: chartColors[2]
            },
            hoverinfo: !showSpikeLines && 'none',
            connectgaps: true
          }
        );

        traces.push(
          { // rate
            x: X,
            y: YRate,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: 'Slurry Rate (m3/min)',
            line: {
              width: 2,
              color: chartColors[3]
            },
            hoverinfo: !showSpikeLines && 'none',
            connectgaps: true
          }
        );

        traces.push(
          { // cum vol
            x: X,
            y: YCumVol,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y3',
            name: 'Cumulative Slurry Volume (m3)',
            line: {
              width: 2,
              color: chartColors[5]
            },
            hoverinfo: !showSpikeLines && 'none',
            connectgaps: true
          }
        );
        
        const XMonitor = [];
        const YCasingPressure = [];

        // Get the timezone offset in minutes and convert to milliseconds
        const timezoneOffset = new Date(monitorData?.[0]?.timestamp).getTimezoneOffset() * 60000;
        
        monitorData?.forEach(d => {
          XMonitor.push(new Date(d.timestamp + ((client === 'yoho' || currentRTFO?.name === 'Test-2') ? 0 : timezoneOffset)));
          YCasingPressure.push(d.casingPressure * 1000);
          // YTubingPressure.push(d.tubingPressure);
          // YPressurePrime.push(d.pressurePrime || d.casingPressurePrime);
        });

        traces.push({ // casing pressure
          x: XMonitor,
          y: YCasingPressure?.length > 0 ? YCasingPressure : [0],
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y4',
          name: `${monitorUWI}-Casing Pr. (MPa)`,
          line: {
            width: 2,
            color: chartColors[1]
          },
          hoverinfo: !showSpikeLines && 'none',
          legendgroup: monitorUWI
        });

        // Calculate smoothed data on high-resolution data only
        if (currentStage?.highResolutionData) {
          // const optionsLong = {
          //   derivative: 0,
          //   windowSize: 121,
          //   polynomial: 1,
          //   pad: 'pre',
          //   padValue: 'symmetric'
          // };

          // let smoothedData = savitzkyGolay(YCasingPressure, 1, optionsLong);
          // smoothedData = savitzkyGolay(smoothedData, 1, optionsLong);
          // smoothedData = savitzkyGolay(smoothedData, 1, optionsLong);

          // const dataForSlopeFitting = smoothedData?.map((d, i) => ({x: new Date(XMonitor?.[i])?.getTime(), y: d}));
  
          
          
          // function findSlopeChanges(data, windowSize, threshold) {
          //   try {
          //     const slopeChanges = [];
          //     const slopes = [];
  
          //     for (let i = windowSize; i < data.length; i++) {
          //       const currentPoint = data[i];
          //       const start = i - windowSize;
          //       const end = i - 1;
            
          //       // Calculate the average slope over the specified window
          //       const averageSlope = calculateAverageSlope(data, start, end);
  
          //       // Check if the absolute difference in slope is above the threshold
          //       const currentSlope = calculateSlope(data[end], currentPoint);
          //       if (Math.abs(currentSlope - averageSlope) > threshold) {
          //         if (slopeChanges?.length === 0) {
          //           slopeChanges.push(currentPoint);
          //           slopes.push(currentSlope);
          //         } else if (Math.abs(currentPoint?.x - slopeChanges?.[slopeChanges?.length - 1]?.x > 5 * 60 * 1000) && haveOppositeSigns(currentSlope, slopes?.[slopes?.length - 1])) {
          //           slopeChanges.push(currentPoint);
          //           slopes.push(currentSlope);
          //         };
          //       };
          //     };

          //     return slopeChanges;

          //   } catch(error) {
          //     console.log(error);
          //   };
            
          //   function calculateAverageSlope(data, start, end) {
          //     try {
          //       let sumSlope = 0;
                
          //       for (let i = start + 1; i <= end; i++) {
          //         sumSlope += calculateSlope(data[i - 1], data[i]);
          //       };
                
          //       return sumSlope / (end - start);

          //     } catch(error) {
          //       console.log(error);
          //       return 0;
          //     };
          //   };

          //   function calculateSlope(point1, point2) {
          //     return (point2.y - point1.y) / (point2.x - point1.x);
          //   };

          //   function haveOppositeSigns(value1, value2) {
          //     return (value1 >= 0 && value2 < 0) || (value1 < 0 && value2 >= 0);
          //   };
          // };
          

          // const slopeChangePoints = findSlopeChanges(dataForSlopeFitting, 90, 0.1E-9);
          // setInflectionPoints(slopeChangePoints);

          traces.push({ // smoothed casing pressure
            x: XMonitor,
            y: smoothedData,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y3',
            name: `${monitorUWI}-Casing Pr. (MPa)`,
            line: {
              width: 3,
              color: chartColors[4]
            },
            hoverinfo: !showSpikeLines && 'none'
          });

        };
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [pumpingData, showSpikeLines, monitorData, monitorUWI, currentStage?.highResolutionData, client, currentRTFO?.name, smoothedData]);


  // // Calculate infliction points
  // const inflectionPoints = useMemo(() => {
    
  //   if (!currentStage?.highResolutionData) return [];

  //   const dataForSlopeFitting = smoothedData?.map((d, i) => ({x: new Date(monitorData?.[i]?.timestamp)?.getTime(), y: d}));
  //   const slopeChangePoints = findSlopeChanges(dataForSlopeFitting, 90, 0.1E-9);
  //   return slopeChangePoints;
    
  // }, [currentStage?.highResolutionData, monitorData, smoothedData]);


  // useEffect(() => {

  //   if (!inflectionPoints?.length > 0 || monitorWellEvents?.find(e => e?.stageName === currentStage?.name && e?.monitorUWI === monitorUWI)) return;

  //   let newMonitorWellEvents = [];
  //   newMonitorWellEvents.push({timestamp: pumpingData?.find(d => d?.pressure > 1)?.timestamp});
  //   newMonitorWellEvents = newMonitorWellEvents.concat(inflectionPoints?.map(p => ({timestamp: p?.x})));
  //   newMonitorWellEvents.sort((a, b) => a.timestamp - b.timestamp);

  //   // Calculate cum. proppant
  //   const cumProppantArray = [];
  //   pumpingData?.forEach((d, i) => {
  //     const proppantMass = (((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - pumpingData?.[i-1]?.time))) * (d?.proppant || 0) / 1000) || 0;
  //     cumProppantArray.push({
  //       timestamp: d.timestamp,
  //       cumProppant: i === 0 ? proppantMass : ((cumProppantArray?.[i-1]?.cumProppant || 0) + proppantMass)
  //     });
  //   });

  //   const startTimestamp = newMonitorWellEvents?.[0]?.timestamp || pumpingData?.[0]?.timestamp;

  //   const calculatedMonitorWellEvents = [];

  //   for (let i = 0; i < newMonitorWellEvents.length; i++) {
      
  //     const eventTimestamp = newMonitorWellEvents[i]?.timestamp;

  //     const eventData = pumpingData?.find(d => d.timestamp >= eventTimestamp - 5000 && d.timestamp <= eventTimestamp + 5000);

  //     // Calculate clean volume
  //     const cleanVolume = (eventData?.cleanVolume - pumpingData?.find(d => d.timestamp >= startTimestamp - 5000 && d.timestamp <= startTimestamp + 5000)?.cleanVolume);
       
  //     // Calculate cum proppant
  //     const cumProppant = (cumProppantArray?.find(d => d.timestamp >= eventTimestamp - 5000 && d.timestamp <= eventTimestamp + 5000)?.cumProppant - cumProppantArray?.find(d => d.timestamp >= startTimestamp - 5000 && d.timestamp <= startTimestamp + 5000)?.cumProppant);

  //     calculatedMonitorWellEvents.push({
  //       name: i === 0 ? 'Stage Start' : `Monitor Event ${parseInt(i)}`,
  //       stageName: currentStage?.name,
  //       monitorUWI,
  //       date: new Date(eventTimestamp)?.toLocaleString(),
  //       timestamp: eventTimestamp,
  //       deltaTime: i === 0 ? 0 : (eventTimestamp - startTimestamp) / 60000,
  //       pressure: eventData?.pressure || 0,
  //       rate: eventData?.rate || 0,
  //       proppant: eventData?.proppant || 0,
  //       cleanVolume,
  //       cumProppant
  //     });
  //   };

  //   const allMonitorWellEvents = monitorWellEvents?.concat(calculatedMonitorWellEvents);
  //   updateMonitorWellEvents(allMonitorWellEvents);
    
  //   // eslint-disable-next-line
  // }, [inflectionPoints, currentStage?.name, monitorUWI]);

  const layout = {
    // grid: {rows: 3, columns: 2, pattern: 'independent'},
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      showgrid: true,
      color: chartColors[2],
      title: {
        text: 'Treating Pressure (MPa)',
        font: {
          size: 12
        },
      },
      range: [0, 100],
      rangemode: 'nonnegative',
      position: 0,
      anchor: 'x',
      side: 'left',
    },
    yaxis2: { // Rate y axis
      linecolor: chartColors[3],
      showgrid: true,
      color: chartColors[3],
      title: {
        text: 'Slurry Rate (m3/min)',
        font: {
          size: 12
        },
      },
      range: [0, 25],
      rangemode: 'nonnegative',
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0
    },
    yaxis3: { // cum slurry vol y axis
      linecolor: chartColors[5],
      showgrid: false,
      color: chartColors[5],
      title: {
        text: 'Cumulative Slurry Volume (m3)',
        font: {
          size: 12
        },
      },
      // autorange: true,
      range: [0, 1500],
      tick0: 0,
      dtick: 300,
      rangemode: 'nonnegative',
      anchor: 'x',
      overlaying: 'y',
      side: 'right',
      position: 1,
      zeroline: false
    },
    yaxis4: { // Monitor pressure y axis
      linecolor: chartColors[1],
      // gridcolor: darkMode ? 'black' : 'lightgrey',
      showgrid: false,
      color: chartColors[1],
      title: {
        text: 'Monitor Pressure (kPa)',
        font: {
          size: 12
        },
      },
      // autorange: true,
      range: [minMonitorPressure, maxMonitorPressure],
      rangemode: 'nonnegative',
      anchor: 'free',
      overlaying: 'y',
      side: 'right',
      position: 1
    },
    xaxis: { // Date x axis
      type: 'date',
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: "Pressure Interference",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber} | Observation Well: ${monitorUWI}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    shapes: [
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.07 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  if (monitorWellEvents?.length > 0) {
    if (showBackgroundMonitors) {

      const backgroundMonitorEvents = monitorWellEvents.filter(event => event?.name !== 'Stage Start' && event?.stageName === currentStage?.name && event?.monitorUWI !== monitorUWI);
      const backgroundLines = backgroundMonitorEvents?.map(event => {
        return {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(event.timestamp),
          y0: 0,
          x1: new Date(event.timestamp),
          y1: 1,
          line: {
            color: 'lightgrey',
            width: 2,
            dash: 'dot'
          }
        };
      });
      const backgroundAnnotations = backgroundMonitorEvents?.map(event => {
        return {
          x: new Date(event.timestamp),
          y: 1.01,
          xref: 'x',
          yref: 'paper',
          text: `${event?.monitorUWI} - ${event?.name?.split(' ')?.slice(-1)?.[0]}`,
          textangle: '-90',
          bgcolor: (darkMode ? '#37474f' : '#fff'),
          font: {color: 'lightgrey'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        };
      });
      layout.shapes = layout.shapes.concat(backgroundLines);
      layout.annotations = layout.annotations.concat(backgroundAnnotations);
    };

    const currentMonitorEvents = monitorWellEvents.filter(event => event?.stageName === currentStage?.name && event?.monitorUWI === monitorUWI);
    const newLines = currentMonitorEvents?.map(event => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event.timestamp),
        y0: 0,
        x1: new Date(event.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newAnnotations = currentMonitorEvents?.map(event => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: event?.name,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLines);
    layout.annotations = layout.annotations.concat(newAnnotations);

  };


  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Pressure-Analysis ${(new Date().toISOString().split('T')[0])}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={handlePlotClick}
    />
  );

  function handlePlotClick(e) {
    try {
      if (!eventEditMode) return;

      // get a timestamp of the clicked point
      const newTimestamp = new Date(e.points?.find(point => point?.data?.name === 'Treating Pr. (MPa)')?.x)?.getTime();
      
      if (!newTimestamp > 0) return;

      const existingTimestamp = monitorWellEvents?.find(t => t.stageName === currentStage?.name && t?.monitorUWI === monitorUWI && Math.abs(t?.timestamp - newTimestamp) <= 30000);


      let newMonitorWellEvents = JSON.parse(JSON.stringify(monitorWellEvents?.filter(e => e?.stageName === currentStage?.name && e?.monitorUWI === monitorUWI)));

      if (existingTimestamp) {
        newMonitorWellEvents = newMonitorWellEvents?.filter(t => t.stageName === currentStage?.name && t?.monitorUWI === monitorUWI && Math.abs(t?.timestamp - newTimestamp) > 30000);
      } else {
        // if adding the first event, automatically add the stage start
        if (newMonitorWellEvents?.length === 0) {
          newMonitorWellEvents.unshift({timestamp: (pumpingData?.find(d => d.rate > 0.5 && d.pressure > 5)?.timestamp || pumpingData?.[0]?.timestamp)});
        };
        newMonitorWellEvents.push({timestamp: newTimestamp});
        newMonitorWellEvents.sort((a, b) => a.timestamp - b.timestamp);
      };

      // Calculate cum. proppant
      const cumProppantArray = [];
      pumpingData?.forEach((d, i) => {
        const proppantMass = (((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - pumpingData?.[i-1]?.time))) * (d?.proppant || 0) / 1000) || 0;
        cumProppantArray.push({
          timestamp: d.timestamp,
          cumProppant: i === 0 ? proppantMass : ((cumProppantArray?.[i-1]?.cumProppant || 0) + proppantMass)
        });
      });

      const startTimestamp = newMonitorWellEvents?.[0]?.timestamp || pumpingData?.[0]?.timestamp;

      const calculatedMonitorWellEvents = [];

      for (let i = 0; i < newMonitorWellEvents.length; i++) {
        
        const eventTimestamp = newMonitorWellEvents[i]?.timestamp;

        const eventData = pumpingData?.find(d => d.timestamp === eventTimestamp);

        // Calculate slurry volume
        const slurryVolume = (eventData?.slurry - pumpingData?.find(d => d.timestamp === startTimestamp)?.slurry) || 0;

        // Calculate clean volume
        const cleanVolume = (eventData?.clean - pumpingData?.find(d => d.timestamp === startTimestamp)?.cleanVolume) || 0;
                
        // Calculate cum proppant
        const cumProppant = (cumProppantArray?.find(d => d.timestamp === eventTimestamp)?.cumProppant - cumProppantArray?.find(d => d.timestamp === startTimestamp)?.cumProppant) || 0;

        calculatedMonitorWellEvents.push({
          name: i === 0 ? 'Stage Start' : `Monitor Event ${parseInt(i)}`,
          stageName: currentStage?.name,
          monitorUWI,
          date: new Date(eventTimestamp)?.toLocaleString(),
          timestamp: eventTimestamp,
          deltaTime: i === 0 ? 0 : (eventTimestamp - startTimestamp) / 60000,
          pressure: eventData?.pressure || 0,
          rate: eventData?.rate || 0,
          proppant: eventData?.proppant || 0,
          slurryVolume,
          cleanVolume,
          cumProppant
        });
      };


      let allMonitorWellEvents = JSON.parse(JSON.stringify(monitorWellEvents?.filter(e => !(e?.stageName === currentStage?.name && e?.monitorUWI === monitorUWI))));
      allMonitorWellEvents = allMonitorWellEvents?.concat(calculatedMonitorWellEvents);

      updateMonitorWellEvents(allMonitorWellEvents);
      updateMonitorWellEventsMap(true);
    } catch (error) {
      console.log(error);
      return;
    };
  };
};

// function findSlopeChanges(data, windowSize, threshold) {
//   try {
//     const slopeChanges = [];
//     const slopes = [];

//     for (let i = windowSize; i < data.length; i++) {
//       const currentPoint = data[i];
//       const start = i - windowSize;
//       const end = i - 1;
  
//       // Calculate the average slope over the specified window
//       const averageSlope = calculateAverageSlope(data, start, end);

//       // Check if the absolute difference in slope is above the threshold
//       const currentSlope = calculateSlope(data[end], currentPoint);
//       if (Math.abs(currentSlope - averageSlope) > threshold) {
//         if (slopeChanges?.length === 0) {
//           slopeChanges.push(currentPoint);
//           slopes.push(currentSlope);
//         } else if (Math.abs(currentPoint?.x - slopeChanges?.[slopeChanges?.length - 1]?.x > 5 * 60 * 1000) && haveOppositeSigns(currentSlope, slopes?.[slopes?.length - 1])) {
//           slopeChanges.push(currentPoint);
//           slopes.push(currentSlope);
//         };
//       };
//     };

//     return slopeChanges;

//   } catch(error) {
//     console.log(error);
//   };
  
//   function calculateAverageSlope(data, start, end) {
//     try {
//       let sumSlope = 0;
      
//       for (let i = start + 1; i <= end; i++) {
//         sumSlope += calculateSlope(data[i - 1], data[i]);
//       };
      
//       return sumSlope / (end - start);

//     } catch(error) {
//       console.log(error);
//       return 0;
//     };
//   };

//   function calculateSlope(point1, point2) {
//     return (point2.y - point1.y) / (point2.x - point1.x);
//   };

//   function haveOppositeSigns(value1, value2) {
//     return (value1 >= 0 && value2 < 0) || (value1 < 0 && value2 >= 0);
//   };
// };